import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectorRef } from '@angular/core';
import { OnChanges, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { orderBy, uniqBy } from 'lodash';
import * as moment from 'moment';
import { Answer, Inspection, InspectionItem, ItemInfo, User } from '../../shared/interfaces';
import { UserService } from '../../user/user.service';
import { VersionService } from '../../version/version.service';
import { InspectionService } from '../inspection.service';

export interface AllNote {
  id?: number;
  inspectionId?: number;
  date?: Date;
  type: string;
  note: string;
  user?: User | string;
  itemIndex?: number;
  sectionIndex?: number;
  questionIndex?: number;
  questionId?: number;
  groupId?: number;
  flagId?: number;
  reason?: string;
}

@Component({
  selector: 'app-inspection-notes',
  templateUrl: './inspection-notes.component.html',
  styleUrls: ['./inspection-notes.component.scss'],
})
export class InspectionNotesComponent implements OnInit, OnChanges {
  constructor(
    public inspectionService: InspectionService,
    public userService: UserService,
    public route: ActivatedRoute,
    private router: Router,
    private versionService: VersionService,
    public changeDetector: ChangeDetectorRef,
  ) {}

  inspection: Inspection;
  answers: Answer[];
  items: Array<InspectionItem & { info?: ItemInfo; index?: number }> = [];

  allNotes: AllNote[] = [];
  filteredNotes: AllNote[] = [];
  selectedTypes = [];
  showFilterDropdown = false;
  expandedNotes: Set<string> = new Set();

  noteTypes: any[] = [
    {
      label: 'Scheduling Notes',
      type: 'scheduling',
      count: 0,
    },
    {
      label: 'Inspection Created Notes',
      type: 'inspection',
      count: 0,
    },
    {
      label: 'Question Comments/Notes',
      type: 'answer',
      count: 0,
    },
    {
      label: 'Flag Notes',
      type: 'flag',
      count: 0,
    },
    {
      label: 'Flag Group Internal Notes',
      type: 'flag-group',
      count: 0,
    },
    {
      label: 'Flag Internal Notes',
      type: 'flag-internal',
      count: 0,
    },
    {
      label: 'Underwriting Review Comments',
      type: 'flag-review',
      count: 0,
    },
  ];

  allFlags = [];
  allComments = [];

  @Input() showFilter = true;
  @Input() vendorMode = false;
  @Output() noteClicked = new EventEmitter<void>();
  @Input() inspectors: any[] = [];

  ngOnInit() {
    this.route.params.subscribe(async params => {
      console.log('params', params);
      if (params.id !== 'add') {
        this.inspection = await this.inspectionService.get(params.id);

        if (this.vendorMode) {
          // Vendor mode: only load scheduling notes
          await this.loadNotesFromInspection();
          // Create notes
          await this.createInspectionNote();
        } else {
          // Admin mode: load all notes
          this.answers = await this.inspectionService.answers(this.inspection.id);
          await this.loadItems();
          await this.mergeAnswers();
          await this.getFlagsAndComments();

          // Create notes
          await this.createInspectionNote();
          this.createAnswerNotes();
          this.createFlagNotes();
          this.createFlagGroupNotes();
          this.createUnderwritingReviewNotes();
          await this.loadNotesFromInspection();
        }

        this.arrangeNotes();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['inspectors']) {
      if (this.allComments.length > 0 && this.allNotes.length > 0) {
        this.allNotes = this.allNotes.filter(note => note.type !== 'answer');
        this.createAnswerNotes();
        this.arrangeNotes();
        this.filterNotes();
      }
    }
  }

  private arrangeNotes() {
    this.allNotes = this.allNotes.map(note => ({
      ...note,
      date: note.date instanceof Date ? note.date : new Date(note.date),
    }));

    this.allNotes = orderBy(this.allNotes, ['date'], ['desc']);
    this.filteredNotes = [...this.allNotes];
    this.noteTypes.forEach(type => {
      type.count = this.allNotes.filter(x => x.type === type.type && x.note !== null && x.note !== '').length;
    });
  }

  // Load data

  async loadItems() {
    this.items = [];
    // Clearing tree cache because its causing multiple issues with loading data
    this.versionService.treeCache = [];
    const items = (await this.versionService.tree(this.inspection.versionId)).items;
    // filter empty sections
    items.forEach(item => {
      item.sections = item.sections.filter(section => section.questions.length);
    });
    // filter empty items
    items.forEach((item: any, i) => {
      if (!item.sections.length) {
        delete items[i];
      } else {
        item.info = this.inspection.items.find(info => info.itemId === item.id);
      }
    });
    items.forEach((item: InspectionItem & { info: ItemInfo }) => {
      if (!item.info) {
        return;
      }
      if (item.info.quantity === 1) {
        this.items.push({
          ...item,
        });
      } else if (item.info.quantity > 1) {
        for (let i = 0; i < item.info.quantity; i++) {
          this.items.push({
            ...JSON.parse(JSON.stringify(item)),
            index: i + 1,
          });
        }
      }
    });
  }

  async loadNotesFromInspection() {
    try {
      const apiNotes = await this.inspectionService.getNotes(this.inspection.id);

      const convertedNotes: AllNote[] = apiNotes.map(note => ({
        id: note.id,
        inspectionId: note.inspectionId,
        date: new Date(note.createdAt || note.date),
        type: 'scheduling',
        reason: note.reason,
        note: note.content || note.note,
        user: note.user,
      }));

      this.allNotes = [...this.allNotes, ...convertedNotes];
      this.filteredNotes = [...this.allNotes];
      this.filterNotes();
    } catch (error) {
      console.error('Error loading notes from API:', error);
    }
  }

  addNewNote(createdNote: any): void {
    const newNote: AllNote = {
      id: createdNote.id,
      inspectionId: createdNote.inspectionId,
      date: new Date(createdNote.createdAt || createdNote.date),
      type: 'scheduling',
      note: createdNote.content || createdNote.note,
      reason: createdNote.reason,
      user: createdNote.user,
    };

    this.allNotes.unshift(newNote);
    this.filteredNotes = [...this.allNotes];
    this.noteTypes.forEach(type => {
      type.count = this.allNotes.filter(x => x.type === type.type && x.note !== null && x.note !== '').length;
    });
    this.filterNotes();
  }

  // tslint:disable:forin
  mergeAnswers() {
    for (const iItem in this.items) {
      const item = this.items[iItem];
      const sections = item.sections;
      const buildingIndex = item.index;

      for (const iSection in sections) {
        const section = sections[iSection];
        const questions = section.questions;

        for (const iQuestion in questions) {
          const question = questions[iQuestion];
          if (question.repeat && question.repeat.length) {
            question.repeat.forEach(() => {
              question.answer = this.answers.filter(ans => {
                return ans.questionId === question.id;
              });
            });
          } else {
            const answer = this.answers.find(ans => {
              const indexOk = !buildingIndex || ans.buildingIndex === buildingIndex;
              return ans.questionId === question.id && indexOk;
            });
            if (answer) {
              question.answer = answer;
            }
          }
          const answers = Array.isArray(question.answer) ? question.answer : [question.answer];
          answers.forEach(answer => {
            if (answer) {
              answer.question = question;
              // set flags & comments' deeplink
              [...answer.flags, ...answer.comments].forEach((x: any) => {
                x.deeplink = [+iItem, +iSection, +iQuestion];
              });
            }
          });
        }
      }
    }
  }

  // tslint:disable:forin
  getFlagsAndComments() {
    this.allFlags = [];
    this.allComments = [];

    for (const iItem in this.items) {
      const item = this.items[iItem];
      const sections = item.sections;

      for (const iSection in sections) {
        const section = sections[iSection];
        const questions = section.questions;

        const sectionFlags = [];
        const sectionComments = [];

        for (const iQuestion in questions) {
          const question = questions[iQuestion];
          const flags = this.getFlags(question);
          const comments = this.getComments(question);

          sectionFlags.push(
            ...flags.map(f => ({
              ...f,
              flag: f,
              question: question.text,
            })),
          );

          sectionComments.push(
            ...comments.map(f => ({
              ...f,
              question: question.text,
            })),
          );
        }

        if (sectionFlags.length) {
          this.allFlags.push(sectionFlags);
        }

        if (sectionComments.length) {
          this.allComments.push(sectionComments);
        }
      }
    }
  }

  // Create Notes

  async createInspectionNote() {
    // Check if inspection was created after August 18th - if so, don't create note as per the requirement AI-4143
    const cutoffDate = moment('2025-09-26').startOf('day');
    const inspectionDate = moment(this.inspection.createdAt).startOf('day');

    if (inspectionDate.isAfter(cutoffDate)) {
      return; // Don't create note for dates after August 18th as per the requirement AI-4143
    }

    const inspectionChanges = await this.inspectionService.changes(this.inspection.id);

    const firstChanged = moment(inspectionChanges[0].date).format('MM-DD-YYYY');
    let createdBy = inspectionChanges[0].username;

    if (firstChanged) {
      createdBy = inspectionChanges[0].username;
    }

    for (const change of inspectionChanges) {
      if (change.changes && change.changes.notes) {
        createdBy = change.username;
      }
    }
    this.allNotes.push({
      inspectionId: this.inspection.id,
      date: this.inspection.createdAt,
      type: 'inspection',
      note: this.inspection.notes,
      user: createdBy,
    });
  }

  createAnswerNotes() {
    for (const comments of this.allComments) {
      for (const comment of comments) {
        const index = comment.deeplink;
        let commentText = comment.text === ' ' ? null : comment.text;
        if (comment.qIndex) {
          commentText = `${commentText} - Building #${comment.qIndex}`;
        }
        this.allNotes.push({
          inspectionId: this.inspection.id,
          date: comment.date,
          type: 'answer',
          note: commentText,
          user: this.getCommentUser(comment),
          itemIndex: index[0],
          sectionIndex: index[1],
          questionIndex: index[2],
          questionId: comment.questionId,
        });
      }
    }
  }

  getCommentUser(comment: any): User | string {
    const inspector = this.inspectors.find((inspector: any) => inspector.id === comment.userId);
    if (inspector) {
      return { name: inspector.name, type: inspector.type, id: inspector.id } as User;
    }
    return comment.username;
  }

  createFlagNotes() {
    for (const flags of this.allFlags) {
      for (const flag of flags) {
        const index = flag.deeplink;

        if (flag.text && flag.text.length > 0) {
          let flagDate = flag.date;
          const flagUser = flag.user ? this.getUserDisplayName(flag.user) : 'Auto flag';
          const flagChanges = flag.changes;
          for (const change of flagChanges) {
            if (change.changes && change.changes.Text) {
              flagDate = change.date;
            }
          }

          this.allNotes.push({
            inspectionId: this.inspection.id,
            date: flagDate,
            type: 'flag',
            note: flag.text,
            user: flagUser,
            itemIndex: index[0],
            sectionIndex: index[1],
            questionIndex: index[2],
            questionId: flag.questionId,
          });
        }
      }
    }
  }

  createFlagGroupNotes() {
    const flagsWithViolation = [];
    for (const flags of this.allFlags) {
      for (const flag of flags) {
        if (flag.violation.internalNotes && flag.violation.internalNotes.length > 0) {
          flagsWithViolation.push({
            ...flag,
          });
        }
      }
    }
    const flagsWithGroup = flagsWithViolation.filter(f => f.groupId);
    const flagsWithoutGroup = flagsWithViolation.filter(f => !f.groupId);

    const uniqueFlagsWithGroup = uniqBy(flagsWithGroup, flag => `${flag.groupId}_${flag.violation.internalNotes}`);

    const allFlags = [...uniqueFlagsWithGroup, ...flagsWithoutGroup];

    for (const flag of allFlags) {
      let flagDate = flag.date;
      const flagUser = flag.user ? this.getUserDisplayName(flag.user) : 'Auto flag';
      const flagChanges = flag.changes;
      for (const change of flagChanges) {
        if (
          change.changes &&
          change.changes['Internal Notes'] &&
          change.changes['Internal Notes'].after &&
          change.changes['Internal Notes'].after.length > 0
        ) {
          flagDate = change.date;
        }
      }
      this.allNotes.push({
        inspectionId: this.inspection.id,
        date: flagDate,
        type: flag.groupId ? 'flag-group' : 'flag-internal',
        note: flag.violation.internalNotes,
        user: flagUser,
        flagId: flag.id,
        groupId: flag.groupId || null,
      });
    }
  }

  createUnderwritingReviewNotes() {
    for (const flags of this.allFlags) {
      for (const flag of flags) {
        if (flag.comments && flag.comments.length > 0) {
          this.allNotes.push({
            inspectionId: this.inspection.id,
            date: flag.comments[0].date,
            type: 'flag-review',
            note: flag.comments[0].text,
            user: flag.comments[0].username,
            flagId: flag.id,
          });
        }
      }
    }
  }

  // Filter

  filterNotes() {
    if (this.selectedTypes.length === 0) {
      this.filteredNotes = [...this.allNotes];
    } else {
      this.filteredNotes = this.allNotes.filter(note => {
        return this.selectedTypes.includes(note.type);
      });
    }

    // Ensure all dates are Date objects and sort chronologically
    this.filteredNotes = this.filteredNotes.map(note => ({
      ...note,
      date: note.date instanceof Date ? note.date : new Date(note.date),
    }));

    this.filteredNotes = orderBy(this.filteredNotes, ['date'], ['desc']);
    this.changeDetector.detectChanges();
    console.log('allNotes', this.allNotes);
  }

  // Utils functions

  getComments(q) {
    if (q?.answer) {
      if (Array.isArray(q.answer)) {
        let i = 1;
        return q.answer.reduce(
          (acc, cur) => [...acc, ...cur.comments.map(c => ({ ...c, questionId: q.id, qIndex: i++ }))],
          [],
        );
      } else {
        return q.answer.comments.map(c => ({ ...c, questionId: q.id }));
      }
    }
    return [];
  }

  getFlags(q) {
    // Ignoring related
    if (q?.answer) {
      if (Array.isArray(q.answer)) {
        return q.answer.reduce(
          (acc, ans) => [
            ...acc,
            ...ans.flags.filter(f => f.answerId === ans.id).map(f => ({ ...f, questionId: q.id })),
          ],
          [],
        );
      } else {
        return q.answer.flags.filter(f => f.answerId === q.answer.id).map(f => ({ ...f, questionId: q.id }));
      }
    }
    return [];
  }

  formatDate(date: Date) {
    return moment(date).format('MM-DD-YYYY');
  }

  formatTime(date: Date) {
    return moment(date).format('hh:mm A');
  }

  getNoteTypeLabel(type: string): string {
    const typeMap = {
      inspection: 'Inspection created Note',
      answer: 'Question Comment',
      flag: 'Flag Note',
      'flag-group': 'Flag Group Note',
      'flag-internal': 'Internal Note',
      'flag-review': 'Review Comment',
      scheduling: 'Scheduling Notes',
    };
    return typeMap[type] || '';
  }

  getNoteTypeValue(user: User): number {
    // 0 = Admin, 1 = Inspector, 2 = Vendor
    if (typeof user === 'string') {
      return 0;
    }
    if (user.type === 'Inspector') {
      return 1;
    }
    if (user.type === 'Vendor') {
      return 2;
    }
    return 0;
  }

  getTagIconSrc(typeValue: number): string {
    // Vendor uses admin icon as requested
    if (typeValue === 2) {
      return 'assets/img/admin-note.svg';
    }
    return typeValue === 0 ? 'assets/img/admin-note.svg' : 'assets/img/inspector-note.svg';
  }

  getTagIconClass(typeValue: number): string {
    if (typeValue === 2) {
      return 'vendor-icon';
    }
    return '';
  }

  getTagTextClass(typeValue: number): string {
    if (typeValue === 2) {
      return 'vendor-text';
    }
    return typeValue === 0 ? 'admin-text' : 'inspector-text';
  }

  getTagText(typeValue: number): string {
    if (typeValue === 2) {
      return 'Vendor note';
    }
    return typeValue === 0 ? 'Admin note' : 'Inspector note';
  }

  getUserDisplayName(user: User | string | undefined): string {
    if (!user) return 'Unknown User';

    if (typeof user === 'string') {
      return user;
    }

    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }

    if (user.name) {
      return user.name;
    }

    return 'Unknown User';
  }

  shouldShowReadMore(text: string): boolean {
    if (!text) return false;
    const charCount = text.trim().length;
    const shouldShow = charCount > 100; // Show read more if more than 100 characters

    return shouldShow;
  }

  getTruncatedText(text: string): string {
    if (!text) return '';
    const charCount = text.trim().length;
    if (charCount <= 100) return text;

    const result = text.substring(0, 100) + '...';
    return result;
  }

  getTruncatedMetadataText(text: string): string {
    if (!text) return '';
    const charCount = text.trim().length;
    if (charCount <= 15) return text;

    return text.substring(0, 15) + '...';
  }

  isExpanded(note: AllNote): boolean {
    if (!note) return false;
    const noteKey = this.createNoteKey(note);
    const expanded = this.expandedNotes.has(noteKey);
    return expanded;
  }

  toggleNoteExpansion(note: AllNote): void {
    if (!note) return;
    const noteKey = this.createNoteKey(note);
    if (this.expandedNotes.has(noteKey)) {
      this.expandedNotes.delete(noteKey);
    } else {
      this.expandedNotes.add(noteKey);
    }
  }

  private createNoteKey(note: AllNote): string {
    const type = note.type || 'unknown';
    const content = note.note || '';
    const date = note.date ? note.date.toString() : '';
    const user = this.getUserDisplayName(note.user);

    return `${type}_${content.substring(0, 50)}_${date}_${user}`.replace(/\s+/g, '_');
  }

  toggleFilterDropdown() {
    this.showFilterDropdown = !this.showFilterDropdown;
  }

  getFilterText(): string {
    if (this.selectedTypes.length === 0) {
      return 'Filter by Type';
    } else if (this.selectedTypes.length === 1) {
      const type = this.noteTypes.find(t => t.type === this.selectedTypes[0]);
      return type ? type.label : 'Filter by Type';
    } else {
      return `${this.selectedTypes.length} types selected`;
    }
  }

  toggleNoteType(type: string) {
    const index = this.selectedTypes.indexOf(type);
    if (index > -1) {
      this.selectedTypes.splice(index, 1);
    } else {
      this.selectedTypes.push(type);
    }
    this.filterNotes();
  }

  clearFilter() {
    this.selectedTypes = [];
    this.filterNotes();
  }

  scrollToNotes() {
    this.noteClicked.emit();
  }
}
